<script setup>
import { useRoute } from 'vue-router';
import { requestScope, scopedResult } from 'dashboard/attention/requestScope';
/**
 * MvpCustomerIntel.vue -- Customer Intel sidebar panel (redesign #69).
 *
 * Architecture: READ-ONLY. No writes, no mutations, no admin tokens in the browser.
 * Proxy key comes from window.mvpIntelConfig.proxyKey (Rails template, per-account:
 *   account 1 = MVP Motorsports, account 2 = PF -- resolved in vueapp.html.erb).
 *
 * Redesign #69 -- information architecture:
 *   Default-visible (1-5): IDENTITY+VALUE, VEHICLE/FITMENT, LIVE CONTEXT,
 *                           ACTIVE CART (if real data), CURRENT ORDER
 *   Collapsed (6-9):        ORDER HISTORY, BEHAVIOR, RELATED CHATS, PRE-CHAT LEAD detail
 *   Modals (overlay):       Order Detail, Agent Override
 *
 * Additive changes (2026-06-22):
 *   C1: Order match/verify tier badge on Current Order + manual order lookup
 *   C2: Visitor Journey / current page section with sanitized clickable links
 *   C3: Blank/dash cleanup — hide empty rows; single quiet "No data" per section
 *   C4: Collapsed debug panel showing raw IntelProxy JSON
 */
import { currentAccountConfig, configuredAccountId } from 'dashboard/attention/accountConfig';
import { ref, watch, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConfidenceBadge from './shared/ConfidenceBadge.vue';
import CustomerSnapshotPanel from './shared/CustomerSnapshotPanel.vue';
import CompactEmptyState from './shared/CompactEmptyState.vue';
import AgentActionsPanel from './shared/AgentActionsPanel.vue';
import AgentShoppingActions from './shared/AgentShoppingActions.vue';
import RelatedConversationsPanel from './shared/RelatedConversationsPanel.vue';
import VehicleFitmentContextPanel from './shared/VehicleFitmentContextPanel.vue';

const intelRoute = useRoute();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const contactGetter = useMapGetter('contacts/getContact');

const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactEmail = computed(
  () => contact.value?.email || currentChat.value?.meta?.sender?.email || ''
);
const conversationId = computed(() => currentChat.value?.id);

// --- State ---
const loading = ref(false);
const error = ref('');
const data = ref(null);

// Order modal state
const orderModalOpen = ref(false);
const orderDetail = ref(null);
const orderLoading = ref(false);

// Accordion expand state -- sections 6-9 default closed
const historyOpen = ref(false);
const behaviorOpen = ref(false);
const chatsOpen = ref(false);
const leadDetailOpen = ref(false);

// Legacy accordion refs kept for compatibility
const visitorOpen = ref(false);
const activityOpen = ref(false);

// Cart "show all" toggle
const cartExpanded = ref(false);

// --- Phase 4: Bridge sections accordion state ---
const bridgeGarageOpen = ref(false);
const bridgeRecentOpen = ref(false);
const bridgeSavedOpen = ref(false);
const bridgeOpsOpen = ref(false);

// Bridge identity confirmation state
const bridgeConfirmBusy = ref(false);
const bridgeConfirmResult = ref(''); // '', 'ok', or an error string

// --- C4: Debug panel toggle ---
const debugOpen = ref(false);
// Raw-data JSON dump is engineer-only: hidden from agents unless a debug flag is set.
const showDebugPanel = computed(() => !!(intelCfg().debug || (typeof window !== 'undefined' && window.mvpIntelDebug)));

// --- ENH-4: Escalations section state ---
const escalationsOpen = ref(false);
const escalationsData = ref(null); // null = not yet fetched
const escalationsLoading = ref(false);

// --- Config from Rails layout (per-account: acct1=MVP, acct2=PF). HOISTED here (above the first
//     immediate:true watch) because that watch synchronously calls fetchEscalations->intelCfg during
//     setup; defining intelCfg later caused a setup-time TDZ (Cannot access before initialization). ---
// --- Config from Rails layout (per-account: acct1=MVP, acct2=PF) ---
const intelCfg = () => currentAccountConfig(window.mvpIntelConfigByAccount, currentChat.value?.account_id, intelRoute.path);

// BFF read (Option B security): same-origin, session-authed via the dashboard's window.axios. The server
// resolves customer/order identity from the conversation - the browser holds NO Magento key for these reads.
const bffAccountId = () => configuredAccountId(window.mvpIntelConfigByAccount, currentChat.value?.account_id, intelRoute.path);
const bffGet = async (action, extra = {}, convId = conversationId.value) => {
  const aid = bffAccountId();
  if (!aid || !convId) throw new Error('bff: no account/conversation');
  const resp = await window.axios.get(`/api/v1/accounts/${aid}/nextlevel/agent/intel`, {
    params: { proxy_action: action, conversation_id: String(convId), ...extra },
  });
  return resp.data;
};
// BFF write (Option B): same-origin, session-authed via window.axios; browser holds NO Magento token.
const bffPost = async (action, params = {}) => {
  const aid = bffAccountId();
  const convId = conversationId.value;
  if (!aid || !convId) throw new Error('bff: no account/conversation');
  const { data } = await window.axios.post(`/api/v1/accounts/${aid}/nextlevel/agent/write`, {
    proxy_action: action, conversation_id: String(convId), ...params,
  });
  return data;
};

const intelContext = computed(() => `${intelRoute.params.accountId}:${currentChat.value?.account_id}:${conversationId.value}`);
const beginIntelRequest = requestScope(() => intelContext.value);

const fetchEscalations = async convId => {
    const validRequest = beginIntelRequest('fetchEscalations');
  if (!convId) return;
  escalationsLoading.value = true;
  try {
    const json = await scopedResult(validRequest, bffGet('escalations', {}, convId));
    escalationsData.value = json.ok ? json.escalations || [] : [];
  } catch (e) {
    if (!validRequest()) return;
    escalationsData.value = [];
  } finally {
    if (validRequest()) {
    escalationsLoading.value = false;
  
    }}
};

// Fetch escalations whenever the conversation changes
watch(
  [intelContext, conversationId],
  ([, convId]) => {
    escalationsData.value = null;
    if (convId) fetchEscalations(convId);
  },
  { immediate: true }
);

const openEscalations = computed(() =>
  (escalationsData.value || []).filter(e => e.status !== 'resolved')
);
const hasOpenEscalations = computed(() => openEscalations.value.length > 0);

// --- ENH-6: RMA / support topic from conversation custom_attributes ---
const supportTopic = computed(() => ca.value.support_topic || '');
const supportTopicLabel = computed(() => {
  const map = {
    order_status: 'Order Status',
    tracking: 'Tracking',
    damaged_item: 'Damaged Item',
    missing_item: 'Missing Item',
    wrong_item: 'Wrong Item',
    rma_request: 'RMA Request',
    cancel_request: 'Cancel Request',
    address_change: 'Address Change',
    fitment_help: 'Fitment Help',
    warranty: 'Warranty',
    invoice_request: 'Invoice Request',
  };
  return map[supportTopic.value] || supportTopic.value.replace(/_/g, ' ');
});
const supportTopicOrderVerified = computed(
  () =>
    ca.value.support_topic_order_verified === 1 ||
    ca.value.support_topic_order_verified === '1'
);

// --- ENH-8: QA outcome chips ---
const qaEnabled = computed(
  () => !!intelCfg().qaEnabled
);
const convResolved = computed(() => {
  const s = currentChat.value?.status;
  return s === 'resolved';
});
const qaOutcomeOpen = ref(false);
const qaOutcomeCodes = ref([]);

const QA_OUTCOME_CODES = [
  { code: 'satisfied', label: 'Satisfied' },
  { code: 'converted', label: 'Converted' },
  { code: 'recovered', label: 'Recovered' },
  { code: 'no_sale', label: 'No Sale' },
  { code: 'escalated', label: 'Escalated' },
  { code: 'wrong_part_prevented', label: 'Wrong Part Prevented' },
  { code: 'fitment_reviewed', label: 'Fitment Reviewed' },
  { code: 'refund_avoided', label: 'Refund Avoided' },
];

const submitQaOutcome = async () => {
    const validRequest = beginIntelRequest('submitQaOutcome');
  if (!qaOutcomeCodes.value.length) return;
  const cfg = intelCfg();
  if (!cfg.agentActionsEnabled) return;
  try {
    await scopedResult(validRequest, bffPost('outcomereport', { outcome_codes: JSON.stringify(qaOutcomeCodes.value) }));
    qaOutcomeOpen.value = false;
  } catch (_e) {
    if (!validRequest()) return;
    // silent — non-blocking
  }
};

// --- P3-7: vehicle_context from IntelProxy payload ---
const vehicleContext = computed(() => {
  // Merge IntelProxy garage + intake vehicle into vehicle_context array
  const ctx = [];
  if (intakeVehicle.value) {
    ctx.push({
      source: 'intake',
      label: intakeVehicle.value,
      confidence: 'Confirmed',
      note: 'Customer selected in queue panel',
    });
  }
  const garage = data.value?.garage || [];
  garage.forEach((v, i) => {
    const label = [
      v.year > 0 ? String(v.year) : '',
      v.make,
      v.model,
      v.submodel && v.submodel.toLowerCase() !== 'all' ? v.submodel : '',
    ]
      .filter(Boolean)
      .join(' ');
    if (label) {
      ctx.push({
        source: 'garage',
        label,
        confidence: 'Confirmed',
        note: v.is_default
          ? 'Default saved vehicle'
          : `Saved vehicle #${i + 1}`,
      });
    }
  });
  // Dedup identical vehicles across sources into ONE row (combined source labels) to avoid redundant
  // rows (same car in intake + garage). First source kept for the label; extras listed in the note.
  const SRC = { intake: 'intake form', garage: 'garage', storefront: 'storefront', inferred: 'inferred', ip_only: 'ip lookup' };
  const byLabel = new Map();
  ctx.forEach(v => {
    const key = (v.label || '').toLowerCase();
    const s = SRC[v.source] || v.source;
    if (!byLabel.has(key)) byLabel.set(key, { ...v, _srcs: [s] });
    else { const e = byLabel.get(key); if (!e._srcs.includes(s)) e._srcs.push(s); }
  });
  return Array.from(byLabel.values()).map(({ _srcs, ...v }) =>
    _srcs.length > 1 ? { ...v, note: `Sources: ${_srcs.join(', ')}` } : v
  );
});

const hasVehicleContext = computed(() => vehicleContext.value.length > 0);

// --- C1: Order match/verify state ---
const orderMatchResult = ref(null); // null | { match_tier, evidence, conflicts, masked_meta }
const orderMatchLoading = ref(false);
const orderMatchError = ref('');

// --- C2: Journey/current page helpers ---
// Sanitize a URL: strip tokens/secrets from query params
const sanitizeUrl = url => {
  if (!url) return '';
  try {
    const u = new URL(url, window.location.origin);
    const sensitive = [
      'token',
      'sig',
      'signature',
      'key',
      'secret',
      'auth',
      '_token',
      'api_key',
      'access_token',
    ];
    sensitive.forEach(k => u.searchParams.delete(k));
    return u.toString();
  } catch {
    return url;
  }
};

// --- intelCfg() hoisted above the first immediate watch (TDZ fix) ---

// --- Fetch combined intel ---
const fetchIntel = async (email, convId, orderNo) => {
    const validRequest = beginIntelRequest('fetchIntel');
  if (!email && !orderNo) {
    data.value = null;
    error.value = '';
    return;
  }
  loading.value = true;
  error.value = '';
  data.value = null;
  cartExpanded.value = false;
  historyOpen.value = false;
  behaviorOpen.value = false;
  chatsOpen.value = false;
  leadDetailOpen.value = false;
  visitorOpen.value = false;
  activityOpen.value = false;
  // Reset C1 state on new conversation
  orderMatchResult.value = null;
  orderMatchError.value = '';
  try {
    // BFF: server resolves email + order context from the conversation (email/orderNo args now only gate above)
    const json = await scopedResult(validRequest, bffGet('intel', {}, convId));
    if (!json.ok) throw new Error(json.error || 'server error');
    data.value = json;
  } catch (e) {
    if (!validRequest()) return;
    error.value = e.message || 'Failed to load intel';
  } finally {
    if (validRequest()) {
    loading.value = false;
  
    }}
};

// Fetch order detail for the modal
const fetchOrderDetail = async incrementId => {
    const validRequest = beginIntelRequest('fetchOrderDetail');
  const email = contactEmail.value;
  if (!email) return;
  orderLoading.value = true;
  orderDetail.value = null;
  orderModalOpen.value = true;
  try {
    const json = await scopedResult(validRequest, bffGet('order', { increment_id: incrementId }));
    orderDetail.value = json.order || null;
  } catch (e) {
    if (!validRequest()) return;
    orderDetail.value = { _error: e.message };
  } finally {
    if (validRequest()) {
    orderLoading.value = false;
  
    }}
};

const closeModal = () => {
  orderModalOpen.value = false;
  orderDetail.value = null;
};

// --- C1: Fetch order match/verify for a manual order number ---
const fetchOrderMatch = async incrementId => {
    const validRequest = beginIntelRequest('fetchOrderMatch');
  const email = contactEmail.value;
  const cfg = intelCfg();
  if (!incrementId || !cfg.agentActionsEnabled) return;
  orderMatchLoading.value = true;
  orderMatchResult.value = null;
  orderMatchError.value = '';
  try {
    const json = await scopedResult(validRequest, bffGet('orderlookup', { increment_id: incrementId }));
    orderMatchResult.value = json;
  } catch (e) {
    if (!validRequest()) return;
    orderMatchError.value = e.message || 'Lookup failed';
  } finally {
    if (validRequest()) {
    orderMatchLoading.value = false;
  
    }}
};

// C1: manual order number on the conversation (prechat 'Order #' box / typed / selected).
const manualOrderNumber = computed(() => {
  const ca = currentChat.value?.custom_attributes || {};
  return ca.manual_order_number || ca.selected_order_increment_id || ca.order_number || '';
});

// React to conversation changes. Also re-fetch when an order # arrives with no email so the FULL
// customer intel backfills from the order (agent-approved order#-keyed lookup).
watch(
  [contactEmail, conversationId, manualOrderNumber, intelContext],
  ([email, convId, orderNo]) => fetchIntel(email, convId, orderNo),
  { immediate: true }
);

watch(manualOrderNumber, val => {
  if (val) fetchOrderMatch(val);
  else {
    orderMatchResult.value = null;
    orderMatchError.value = '';
  }
});

// --- Helpers ---
const fmtDate = str => {
  if (!str) return '';
  try {
    return new Date(str).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return str;
  }
};

const fmtRelative = str => {
  if (!str) return '';
  try {
    const diff = Date.now() - new Date(str).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return str;
  }
};

const orderStateClass = state => {
  const map = {
    complete: 'bg-n-teal-5 text-n-teal-12',
    processing: 'bg-n-amber-5 text-n-amber-12',
    canceled: 'bg-n-ruby-5 text-n-ruby-12',
    closed: 'bg-n-slate-5 text-n-slate-12',
    holded: 'bg-n-amber-5 text-n-amber-12',
  };
  return map[state] || 'bg-n-solid-3 text-n-slate-12';
};

const orderStateLabel = (state, status) => {
  if (status) return status.replace(/_/g, ' ');
  return state ? state.replace(/_/g, ' ') : 'New';
};

// C1: Order match badge classes and label
const orderMatchTierClass = computed(() => {
  const tier = orderMatchResult.value?.match_tier;
  switch (tier) {
    case 'VERIFIED_MATCH':
    case 'STRONG_MATCH':
      return 'bg-n-teal-5 text-n-teal-12';
    case 'POSSIBLE_MATCH':
      return 'bg-n-amber-5 text-n-amber-12';
    case 'CONFLICTING_MATCH':
      return 'bg-n-ruby-5 text-n-ruby-12';
    case 'NO_MATCH':
      return 'bg-n-solid-3 text-n-slate-10';
    default:
      return 'bg-n-solid-3 text-n-slate-10';
  }
});

const orderMatchTierLabel = computed(() => {
  const tier = orderMatchResult.value?.match_tier;
  if (!tier) return '';
  return tier
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
});

const isConflictingMatch = computed(
  () => orderMatchResult.value?.match_tier === 'CONFLICTING_MATCH'
);

const hasCart = computed(() => data.value?.cart?.count > 0);

// CustomerSnapshotPanel input: the native Chatwoot Contact header (above) OWNS name/email/location, so the
// snapshot leads with VALUE only (customer group + tenure) to avoid duplicate identity (GPT-5.5 + vision gate).
// Folding the native chrome so the snapshot becomes the sole identity anchor is a later, tested step.
const snapshotCustomer = computed(() => {
  const dc = data.value?.customer || {};
  const merged = { group_id: dc.group_id, created_at: dc.created_at };
  return merged.created_at != null || merged.group_id != null ? merged : null;
});
const hasOrders = computed(() => data.value?.orders?.length > 0);
const hasUpsell = computed(
  () => data.value?.behavior?.repeat_views?.length > 0
);
const hasLastSeen = computed(() => !!data.value?.behavior?.last_seen);
const hasChats = computed(() => data.value?.chats?.threads?.length > 0);
const hasActivity = computed(
  () => data.value?.behavior?.recent_activity?.length > 0
);

const cartItems = computed(() => data.value?.cart?.items || []);
const visibleCartItems = computed(() =>
  cartExpanded.value ? cartItems.value : cartItems.value.slice(0, 3)
);
const hiddenCartCount = computed(() => cartItems.value.length - 3);

const topUpsell = computed(() => {
  const views = data.value?.behavior?.repeat_views || [];
  return views[0] || null;
});

const customerSince = computed(() => {
  const d = data.value?.customer?.created_at;
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return d;
  }
});

// Current order = most recent (first in the orders array)
const currentOrder = computed(() => data.value?.orders?.[0] || null);

// === Phase 2 (A): Immediate Attention -- derived "what matters now" from EXISTING data (no new fetch).
//     Drives the top summary card in both matched and guest branches.

// Order history = remaining orders after the first
const historyOrders = computed(() => data.value?.orders?.slice(1) || []);

// History header summary -- change 3: show only order count (drop $ total + latest # which duplicate Current Order above)
const historyHeaderSummary = computed(() => {
  const orders = data.value?.orders || [];
  if (orders.length <= 1) return '';
  const count = orders.length;
  return `${count} order${count !== 1 ? 's' : ''}`;
});

// Behavior header summary
const behaviorHeaderSummary = computed(() => {
  const top = topUpsell.value;
  if (top) {
    const views = top.view_count || top.views || 0;
    const name = top.name || '';
    return `${name ? name.slice(0, 24) + (name.length > 24 ? '...' : '') : ''} ${views}x`;
  }
  const acts = data.value?.behavior?.recent_activity?.length || 0;
  if (acts) return `${acts} recent event${acts !== 1 ? 's' : ''}`;
  return '';
});

// Typing sneak-peek: live visitor draft from conversation custom_attributes (set by the widget)
const visitorDraft = computed(
  () => currentChat.value?.custom_attributes?.visitor_draft || ''
);

// Live funnel trigger: conversation custom_attributes set by the Magento FunnelNotifierService
const funnelStage = computed(
  () => currentChat.value?.custom_attributes?.funnel_stage || ''
);
const funnelDetail = computed(
  () => currentChat.value?.custom_attributes?.funnel_detail || ''
);
const funnelInCheckout = computed(() => funnelStage.value === 'checkout');
const funnelOrderPlaced = computed(() => funnelStage.value === 'order_placed');

// --- Guided Chat Intake (#68) Phase 4: Pre-Chat Lead awareness + agent override ---
// All values read from the reactive conversation custom_attributes (same WebSocket-pushed
// pattern as visitor_draft / funnel_stage above). The customer-side P2 services write these.
const ca = computed(() => currentChat.value?.custom_attributes || {});

// prechat_state may arrive as a JSON object/string blob (state + summary) OR the discrete
// attributes may be present. Tolerate both shapes so the section shows whenever an intake exists.
const prechatStateRaw = computed(() => {
  const v = ca.value.prechat_state;
  if (!v) return null;
  if (typeof v === 'string') {
    try {
      return JSON.parse(v);
    } catch {
      return { state: v };
    }
  }
  return v;
});

// The section renders when ANY intake signal is present.
const intakeActive = computed(() => {
  const c = ca.value;
  return !!(
    prechatStateRaw.value ||
    c.intake_stage ||
    c.intake_active ||
    c.prechat_name ||
    c.prechat_email ||
    c.prechat_phone ||
    c.prechat_order_number ||
    c.intake_vehicle ||
    c.intake_part
  );
});

// Current state machine state (string), from the blob or a discrete intake_stage attribute.
const intakeStage = computed(
  () =>
    (prechatStateRaw.value && prechatStateRaw.value.state) ||
    ca.value.intake_stage ||
    ''
);

// Captured pre-chat lead fields.
const leadName = computed(
  () =>
    ca.value.prechat_name ||
    (prechatStateRaw.value && prechatStateRaw.value.name) ||
    ''
);
const leadEmail = computed(
  () =>
    ca.value.prechat_email ||
    (prechatStateRaw.value && prechatStateRaw.value.email) ||
    ''
);
const leadPhone = computed(
  () =>
    ca.value.prechat_phone ||
    (prechatStateRaw.value && prechatStateRaw.value.phone) ||
    ''
);
const leadOrder = computed(
  () =>
    ca.value.prechat_order_number ||
    (prechatStateRaw.value && prechatStateRaw.value.order_number) ||
    ''
);

// Required-field completion (name + email are the required pre-chat fields).
const leadRequiredComplete = computed(
  () => !!leadName.value && !!leadEmail.value
);
const leadFieldCount = computed(() => {
  let n = 0;
  if (leadName.value) n += 1;
  if (leadEmail.value) n += 1;
  if (leadPhone.value) n += 1;
  if (leadOrder.value) n += 1;
  return n;
});

// Selected context: vehicle + part (the customer selected/typed during the queue panel).
const intakeVehicle = computed(() => {
  const c = ca.value;
  if (c.intake_vehicle) return c.intake_vehicle;
  const b = prechatStateRaw.value;
  if (b && (b.vehicle_label || b.vehicle)) return b.vehicle_label || b.vehicle;
  if (data.value?.vehicle) return data.value.vehicle;
  return '';
});
const intakePartName = computed(() => {
  const c = ca.value;
  if (c.intake_part_name) return c.intake_part_name;
  if (c.intake_part) return c.intake_part;
  const b = prechatStateRaw.value;
  if (b && (b.part_name || b.part)) return b.part_name || b.part;
  return '';
});
const intakePartNumber = computed(
  () =>
    ca.value.intake_part_number ||
    ca.value.intake_part_sku ||
    (prechatStateRaw.value && prechatStateRaw.value.part_number) ||
    ''
);

// Display name used in the awareness flag (fall back to the contact name, then "Customer").
const flagName = computed(
  () =>
    leadName.value ||
    contact.value?.name ||
    currentChat.value?.meta?.sender?.name ||
    'Customer'
);

// Human-readable awareness flag from the current state machine state.
const awarenessFlag = computed(() => {
  const stage = intakeStage.value;
  const n = flagName.value;
  switch (stage) {
    case 'vehicle_input_started':
      return `${n} is entering vehicle details now`;
    case 'part_input_started':
      return `${n} is entering part details now`;
    case 'optional_context_panel_opened':
    case 'optional_context_cta_shown':
      return `${n} started adding context while waiting`;
    case 'vehicle_selected':
    case 'vehicle_typed':
      return `${n} selected a vehicle`;
    case 'part_selected':
    case 'part_typed':
      return `${n} selected a part`;
    case 'optional_context_saved':
      return `${n} saved details`;
    case 'optional_context_abandoned':
    case 'panel_closed':
      return `${n} left the context panel`;
    case 'prechat_name_entered':
    case 'prechat_email_entered':
    case 'prechat_started':
    case 'prechat_required_complete':
      return `${n} is filling in contact details`;
    case 'queued':
      return `${n} is waiting in queue`;
    default:
      return stage ? `${n}: ${stage.replace(/_/g, ' ')}` : '';
  }
});

// A short human label for the state badge.
const stageLabel = computed(() =>
  intakeStage.value ? intakeStage.value.replace(/_/g, ' ') : 'lead forming'
);

// Override is offered while the customer is in the pre-chat / queue / optional-panel flow
// (i.e. not yet in an active two-way conversation).
const overrideOfferable = computed(() => {
  const s = intakeStage.value;
  if (!intakeActive.value) return false;
  const done = [
    'agent_override_to_chat',
    'conversation_active',
    'customer_switched_to_chat',
  ];
  return !done.includes(s);
});

// --- Agent override action ---
const overrideModalOpen = ref(false);
const overrideReason = ref('');
const overrideBusy = ref(false);
const overrideResult = ref(''); // '', 'ok', or an error string
const overrideDone = computed(
  () =>
    intakeStage.value === 'agent_override_to_chat' ||
    !!ca.value.agent_override_requested
);

const intakeVisitorId = computed(
  () =>
    ca.value.intake_visitor_id ||
    ca.value.visitor_id ||
    (prechatStateRaw.value && prechatStateRaw.value.visitor_id) ||
    ''
);

const openOverrideModal = () => {
  overrideReason.value = '';
  overrideResult.value = '';
  overrideModalOpen.value = true;
};
const closeOverrideModal = () => {
  overrideModalOpen.value = false;
};

const submitOverride = async () => {
    const validRequest = beginIntelRequest('submitOverride');
  const cfg = intelCfg();
  const convId = conversationId.value;
  if (!cfg.agentActionsEnabled || !convId) {
    overrideResult.value = 'Override not configured';
    return;
  }
  overrideBusy.value = true;
  overrideResult.value = '';
  try {
    const json = await scopedResult(validRequest, bffPost('override', { visitor_id: intakeVisitorId.value || '', reason: overrideReason.value || '', panel_state: intakeStage.value || '' }).catch(() => ({})));
    if (json.ok) {
      overrideResult.value = 'ok';
      overrideModalOpen.value = false;
    } else {
      overrideResult.value = json.error || 'request failed';
    }
  } catch (e) {
    if (!validRequest()) return;
    overrideResult.value = e.message || 'Override failed';
  } finally {
    if (validRequest()) {
    overrideBusy.value = false;
  
    }}
};

// Whether the pre-chat lead has structured detail beyond just the awareness flag
// (i.e., captured fields or selected vehicle/part context beyond the stage banner).
const leadHasStructuredDetail = computed(() => {
  return (
    leadName.value ||
    leadEmail.value ||
    leadPhone.value ||
    leadOrder.value ||
    intakeVehicle.value ||
    intakePartName.value
  );
});

// --- C2: Visitor Journey computed ---
// Current page from conversation meta or behavior
const currentPageUrl = computed(() => {
  const ca = currentChat.value?.custom_attributes || {};
  const meta = currentChat.value?.meta?.additional_attributes || {};
  // Absolute URL captured by the widget (proactive.phtml current_url = location.href).
  const abs = ca.current_url || meta.current_url || '';
  if (abs) return sanitizeUrl(abs);
  // Fallback: a path -> make absolute via a known recent-page origin (bridge urls are absolute).
  const raw = ca.current_page || ca.last_page || meta.last_page || meta.referer || '';
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return sanitizeUrl(raw);
  const m = (recentPages.value[0]?.url || '').match(/^https?:\/\/[^/]+/);
  return m ? sanitizeUrl(m[0] + (raw.charAt(0) === '/' ? '' : '/') + raw) : '';
});

const currentPageTitle = computed(() => {
  const ca = currentChat.value?.custom_attributes || {};
  const meta = currentChat.value?.meta?.additional_attributes || {};
  return ca.page_title || meta.page_title || meta.title || '';
});

// Recent pages from behavior.recent_activity (type=page_view) or bridge history
const recentPages = computed(() => {
  const activity = data.value?.behavior?.recent_activity || [];
  const pages = activity
    .filter(a => a.type === 'page_view' || a.action === 'page_view' || a.url)
    .slice(0, 5)
    .map(a => ({
      url: sanitizeUrl(a.url || a.target_url || ''),
      title: a.target_name || a.name || a.url || '',
      time: a.created_at || '',
    }))
    .filter(p => p.url);
  return pages;
});

const hasJourneyData = computed(
  () => !!currentPageUrl.value || recentPages.value.length > 0
);

// --- Phase 4: Bridge computed helpers ---

const bridgeIdentity = computed(() => data.value?.bridge?.identity ?? null);

const confidenceBadgeClass = computed(() => {
  const c = bridgeIdentity.value?.confidence;
  switch (c) {
    case 'verified':
      return 'bg-n-teal-5 text-n-teal-12';
    case 'strong':
      return 'bg-n-teal-5 text-n-teal-11';
    case 'medium':
      return 'bg-n-amber-5 text-n-amber-12';
    case 'weak':
      return 'bg-n-ruby-5 text-n-ruby-11';
    default:
      return 'bg-n-solid-3 text-n-slate-10';
  }
});

const confidenceBadgeLabel = computed(() => {
  const c = bridgeIdentity.value?.confidence;
  if (!c || c === 'none') return 'Unverified';
  return c.charAt(0).toUpperCase() + c.slice(1);
});

// Whether to offer Confirm/Override (non-verified, non-busy, not already confirmed this session)
const confirmOfferable = computed(() => {
  const c = bridgeIdentity.value?.confidence;
  return (
    !!c &&
    c !== 'verified' &&
    c !== 'none' &&
    bridgeConfirmResult.value !== 'ok'
  );
});

// Garage vehicles from the bridge (vehicles.garage array)
const bridgeGarage = computed(() => {
  const v = data.value?.bridge?.vehicles;
  const arr = v?.garage ?? [];
  return Array.isArray(arr) ? arr : [];
});

// Recently viewed from bridge history section
const bridgeRecentlyViewed = computed(() => {
  const h = data.value?.bridge?.history;
  const arr = h?.recently_viewed ?? [];
  return Array.isArray(arr) ? arr : [];
});

// Agent shopping actions: products the customer is viewing / has viewed (cart + recently-viewed), deduped.
const shopProducts = computed(() => {
  const seen = new Set();
  const out = [];
  const add = it => {
    const sku = it && it.sku;
    if (!sku || seen.has(sku)) return;
    seen.add(sku);
    out.push({ sku, name: it.name || it.product_name || sku, price: it.price || it.unit_price || 0 });
  };
  (cartItems.value || []).forEach(add);
  (bridgeRecentlyViewed.value || []).forEach(add);
  return out;
});

// Saved for later from bridge commerce section
const bridgeSavedForLater = computed(() => {
  const c = data.value?.bridge?.commerce;
  const arr = c?.saved ?? [];
  return Array.isArray(arr) ? arr : [];
});

// Ops status from bridge related[] (type = order_ops_status)
const bridgeOpsStatus = computed(() => {
  const related = data.value?.bridge?.related ?? [];
  return Array.isArray(related)
    ? related.filter(r => r.type === 'order_ops_status')
    : [];
});

// --- Phase 4: Identity confirm action ---
const submitIdentityConfirm = async action => {
    const validRequest = beginIntelRequest('submitIdentityConfirm');
  const cfg = intelCfg();
  if (!cfg.agentActionsEnabled) {
    bridgeConfirmResult.value = 'Not configured';
    return;
  }
  bridgeConfirmBusy.value = true;
  bridgeConfirmResult.value = '';
  try {
    const json = await scopedResult(validRequest, bffPost('identityconfirm', { action, tier: bridgeIdentity.value?.confidence ?? '' }).catch(() => ({})));
    if (json.ok) {
      bridgeConfirmResult.value = 'ok';
    } else {
      bridgeConfirmResult.value = json.error || 'request failed';
    }
  } catch (e) {
    if (!validRequest()) return;
    bridgeConfirmResult.value = e.message || 'Confirm failed';
  } finally {
    if (validRequest()) {
    bridgeConfirmBusy.value = false;
  
    }}
};

// ===========================================================================
// ENH-1: Automotive skill-based routing -- badge + "Wrong team?" relabel.
// routing_group / routing_reason / routing_confidence are written onto the
// Chatwoot conversation custom_attributes by the Magento routing listener and
// arrive reactively via `ca` (same WebSocket-pushed channel as the rest).
// ===========================================================================
const routingGroup = computed(() => ca.value.routing_group || '');
const routingReason = computed(() => ca.value.routing_reason || '');
const routingConfidence = computed(() =>
  (ca.value.routing_confidence || '').toString().toLowerCase()
);

const routingConfidenceLevel = computed(() => {
  // Map the raw confidence string onto the shared ConfidenceBadge vocabulary.
  switch (routingConfidence.value) {
    case 'confirmed':
      return 'Confirmed';
    case 'high':
      return 'Confirmed';
    case 'likely':
      return 'Likely';
    case 'medium':
      return 'Likely';
    case 'possible':
      return 'Possible';
    case 'low':
      return 'Possible';
    case 'weak':
      return 'Weak';
    default:
      // Item B: unrecognized/empty confidence must read as Unknown, NEVER Weak.
      return 'Unknown';
  }
});

const routingGroupLabel = computed(() =>
  routingGroup.value
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
);

// Phase 2/3: Immediate Attention (relocated below its deps to avoid a TDZ).
const iaIdentityState = computed(() => {
  if (isConflictingMatch.value) return { label: 'Identity Conflict', tone: 'warn' };
  if (data.value?.found) return { label: 'Matched Customer', tone: 'ok' };
  if (data.value && !data.value.found) {
    return contactEmail.value
      ? { label: 'Unmatched', tone: 'muted' }
      : { label: 'Guest', tone: 'muted' };
  }
  return { label: '', tone: 'muted' };
});
const iaIntent = computed(() => {
  if (routingGroup.value) return routingGroupLabel.value;
  const gi = (ca.value.intent || ca.value.guided_intent || '').toString();
  return gi ? gi.replace(/[-_]/g, ' ') : '';
});
const iaWarning = computed(() => {
  if (isConflictingMatch.value) return 'Identity conflict -- order details hidden until verified.';
  const o = currentOrder.value;
  if (o && /pending|unpaid|payment|hold/i.test(String(o.status || ''))) {
    return 'Order may be unpaid/on hold -- verify before promising.';
  }
  if (Number(ca.value.cart_value || 0) >= 500) return 'High-value cart active -- prioritize sales response.';
  if (awarenessFlag.value) return String(awarenessFlag.value);
  if (data.value && !data.value.found && !contactEmail.value) return 'No identity yet -- ask for email or order number.';
  return '';
});
const iaNextAction = computed(() => {
  if (isConflictingMatch.value) return 'Confirm identity before revealing order details.';
  if (data.value && !data.value.found) return 'Ask for email or order number to unlock order context.';
  if (hasCart.value) return 'Review the active cart; help close the sale.';
  if (hasOrders.value) return 'Open the current order for status / fulfillment.';
  if (routingGroup.value) return 'Confirm routing; relabel if wrong team.';
  return 'Greet and identify the customer need.';
});
const showImmediateAttention = computed(
  () => !!(iaIdentityState.value.label || iaIntent.value || iaWarning.value)
);
// Phase 3: state-driven semantic accent for the Immediate Attention card.
const iaToneClass = computed(() => {
  if (isConflictingMatch.value) return 'mvp-ia-danger';
  if (iaWarning.value) return 'mvp-ia-warning';
  return 'mvp-ia-calm';
});
// Phase 3: identity + intent as muted metadata (GPT-vision: action primary, badges fewer).
const iaMeta = computed(() => {
  const parts = [];
  if (iaIdentityState.value.label) parts.push(iaIdentityState.value.label);
  if (iaIntent.value) parts.push(iaIntent.value);
  return parts.join(' \u00b7 ');
});

// Relabel ("Wrong team?") control -- reuses the exact override token + endpoint.
const relabelOpen = ref(false);
const relabelGroup = ref('');
const relabelBusy = ref(false);
const relabelResult = ref(''); // '', 'ok', or an error string

// Item A: relabel write-back requires the per-store override token. When it is absent,
// present a clear disabled note instead of a working-looking control that fails on click.
const relabelConfigured = computed(() => {
  const cfg = intelCfg();
  return !!(cfg && cfg.agentActionsEnabled);
});

// The selectable target queue groups (slugs match routing_groups.yaml on the
// Magento side). Keep generic + automotive-oriented; the server validates.
const RELABEL_GROUPS = [
  { slug: 'engine-driveline', label: 'Engine & Driveline' },
  { slug: 'forced-induction', label: 'Forced Induction' },
  { slug: 'suspension-brakes', label: 'Suspension & Brakes' },
  { slug: 'fitment-help', label: 'Fitment Help' },
  { slug: 'order-support', label: 'Order Support' },
  { slug: 'general', label: 'General' },
];

const openRelabel = () => {
  relabelResult.value = '';
  relabelGroup.value = '';
  relabelOpen.value = !relabelOpen.value;
};

const submitRelabel = async () => {
    const validRequest = beginIntelRequest('submitRelabel');
  const cfg = intelCfg();
  const convId = conversationId.value;
  if (!cfg.agentActionsEnabled || !convId) {
    relabelResult.value = 'Relabel not configured';
    return;
  }
  if (!relabelGroup.value) {
    relabelResult.value = 'Pick a team';
    return;
  }
  relabelBusy.value = true;
  relabelResult.value = '';
  try {
    const json = await scopedResult(validRequest, bffPost('override', { action: 'relabel', new_group: relabelGroup.value, old_group: routingGroup.value || '', reason: 'agent relabel from intel panel' }).catch(() => ({})));
    if (json.ok) {
      relabelResult.value = 'ok';
      relabelOpen.value = false;
      // Optimistically reflect the new group locally; the WebSocket-pushed
      // custom_attributes update will reconcile it authoritatively.
      if (json.routing_group && currentChat.value) {
        currentChat.value.custom_attributes = {
          ...(currentChat.value.custom_attributes || {}),
          routing_group: json.routing_group,
        };
      }
    } else {
      relabelResult.value = json.error || 'request failed';
    }
  } catch (e) {
    if (!validRequest()) return;
    relabelResult.value = e.message || 'Relabel failed';
  } finally {
    if (validRequest()) {
    relabelBusy.value = false;
  
    }}
};

// ===========================================================================
// ENH-2: Triage summary -- one bold sentence in section 9. Primary source is
// the Chatwoot conversation custom_attributes.triage_summary (server-composed);
// fall back to an on-demand action=triage read if the attribute isn't present.
// ===========================================================================
const triageSummaryAttr = computed(() => ca.value.triage_summary || '');
const triageSummaryFetched = ref('');
const triageSummary = computed(
  () => triageSummaryAttr.value || triageSummaryFetched.value || ''
);

const fetchTriage = async convId => {
    const validRequest = beginIntelRequest('fetchTriage');
  if (!convId) return;
  // Only bother if the attribute didn't already carry it.
  if (triageSummaryAttr.value) return;
  try {
    const json = await scopedResult(validRequest, bffGet('triage', {}, convId));
    if (json.ok && json.triage) {
      triageSummaryFetched.value =
        json.triage.summary || json.triage.triage_summary || '';
    }
  } catch (e) {
    if (!validRequest()) return;
    /* best-effort; the section simply stays hidden */
  }
};

watch(
  [intelContext, conversationId],
  ([, convId]) => {
    triageSummaryFetched.value = '';
    if (convId) fetchTriage(convId);
  },
  { immediate: true }
);

// ===========================================================================
// ENH-5: Context-aware suggested macros. The chips arrive in the IntelProxy
// payload as { short_code, reason, source }. Clicking a chip resolves the
// canned response body by short_code (existing store action) and inserts it
// into the active reply editor (same bus event Copilot uses).
// ===========================================================================
const suggestedMacros = computed(() => {
  const arr = data.value?.suggested_macros;
  return Array.isArray(arr) ? arr : [];
});
const hasSuggestedMacros = computed(() => suggestedMacros.value.length > 0);
// Phase 2 (F): cap the rail to a small number; overflow lives in the Customer Context tab.
const RR_MACRO_CAP = 5;
const suggestedMacrosCapped = computed(() => suggestedMacros.value.slice(0, RR_MACRO_CAP));
const suggestedMacroOverflow = computed(() => Math.max(0, suggestedMacros.value.length - RR_MACRO_CAP));

// Map a macro "source" onto the shared ConfidenceBadge vocabulary so the chip
// quietly signals how grounded each suggestion is.
const macroSourceLevel = source => {
  switch ((source || '').toLowerCase()) {
    case 'order_record':
      return 'Confirmed';
    case 'product_attribute':
      return 'Likely';
    case 'freight_rule':
      return 'Likely';
    case 'policy':
      return 'Possible';
    default:
      // Item B: unknown macro source reads as Unknown, not Weak.
      return 'Unknown';
  }
};

const macroInsertBusy = ref(''); // short_code currently inserting
const macroInsertError = ref('');

const insertMacro = async macro => {
    const validRequest = beginIntelRequest('insertMacro');
  const code = macro && macro.short_code;
  if (!code) return;
  macroInsertError.value = '';
  macroInsertBusy.value = code;
  try {
    // Pull the canned responses matching this short_code (existing pattern).
    await scopedResult(validRequest, store.dispatch('getCannedResponse', { searchKey: code }));
    const all = store.getters.getCannedResponses || [];
    // Exact short_code match first; otherwise the first returned record.
    const match =
      all.find(
        c => (c.short_code || '').toLowerCase() === code.toLowerCase()
      ) || all[0];
    if (!match || !match.content) {
      macroInsertError.value = `No canned response for "${code}"`;
      return;
    }
    // Insert into the active reply editor (rich + plain), exactly like Copilot.
    emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, match.content);
    emitter.emit(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, match.content);
  } catch (e) {
    if (!validRequest()) return;
    macroInsertError.value = 'Could not insert macro';
  } finally {
    if (validRequest()) {
    macroInsertBusy.value = '';
  
    }}
};
watch(intelContext, () => {
  data.value = null; error.value = ''; loading.value = false;
  orderModalOpen.value = false; orderDetail.value = null; orderLoading.value = false;
  orderMatchResult.value = null; orderMatchError.value = ''; orderMatchLoading.value = false;
  escalationsData.value = null; escalationsLoading.value = false; triageSummaryFetched.value = '';
}, { flush: 'sync' });
</script>

<template>
  <div class="mvp-customer-intel">
    <!-- ============================================================ -->
    <!-- Loading / error / no-email states                             -->
    <!-- ============================================================ -->
    <div v-if="loading" class="px-4 py-3 space-y-3 animate-pulse" aria-busy="true">
      <div class="space-y-1.5">
        <div class="h-3.5 w-2/5 rounded bg-n-slate-4" />
        <div class="h-2.5 w-3/5 rounded bg-n-slate-3" />
      </div>
      <div class="h-5 w-1/3 rounded bg-n-slate-4" />
      <div class="space-y-2 pt-1">
        <div class="h-2.5 w-full rounded bg-n-slate-3" />
        <div class="h-2.5 w-4/5 rounded bg-n-slate-3" />
        <div class="h-2.5 w-3/5 rounded bg-n-slate-3" />
      </div>
    </div>

    <div
      v-else-if="!contactEmail"
      class="px-4 py-3 text-xs text-n-slate-10 text-center"
    >
      No contact email. Intel unavailable.
    </div>

    <div v-else-if="error" class="px-4 py-2 text-xs text-n-ruby-11">
      {{ error }}
    </div>

    <!-- ============================================================ -->
    <!-- Guest / unmatched visitor                                     -->
    <!-- ============================================================ -->
    <div v-else-if="data && !data.found" class="px-4 py-3 space-y-4">
      <!-- Phase 2 (A): IMMEDIATE ATTENTION (guest) -->
      <div v-if="showImmediateAttention" :class="iaToneClass" class="space-y-1 pb-3 pl-2 border-b border-n-weak">
        <div class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10">Immediate Attention</div>
        <div v-if="iaWarning" class="text-[12px] font-semibold leading-snug" :class="isConflictingMatch ? 'text-n-ruby-11' : 'text-n-amber-11'">{{ iaWarning }}</div>
        <div v-if="iaNextAction" class="leading-snug" :class="iaWarning ? 'text-[11px] text-n-slate-11' : 'text-[12px] font-semibold text-n-slate-12'">{{ iaNextAction }}</div>
        <div v-if="iaMeta" class="text-[11px] text-n-slate-10">{{ iaMeta }}</div>
      </div>
      <!-- Section 1: IDENTITY -- guest one-liner -->
      <div class="space-y-0.5">
        <div
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          Identity
        </div>
        <div class="text-xs text-n-slate-11">
          Guest / not a registered customer
        </div>
      </div>

      <!-- C2: Current page for guest visitors -->
      <div v-if="currentPageUrl" class="space-y-1">
        <div
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          Current Page
        </div>
        <a
          :href="currentPageUrl"
          target="_blank"
          rel="noopener"
          class="block text-xs text-n-brand hover:underline truncate"
          :title="currentPageUrl"
          >{{ currentPageTitle || currentPageUrl }}</a>
      </div>

      <!-- Section 3: LIVE CONTEXT -- intake/funnel signals if present -->
      <div
        v-if="
          intakeActive || funnelInCheckout || funnelOrderPlaced || visitorDraft
        "
        class="space-y-2"
      >
        <div
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          Live
        </div>
        <div
          v-if="funnelInCheckout"
          class="flex items-center gap-1.5 text-[11px] text-n-amber-11 font-semibold"
        >
          <i class="i-lucide-shopping-cart text-n-amber-10 shrink-0" />
          <span>In checkout{{
              data?.cart?.count > 0 && data?.cart?.formatted_total
                ? ' - ' + data.cart.formatted_total + ' cart'
                : ''
            }}</span>
        </div>
        <div
          v-else-if="funnelOrderPlaced"
          class="flex items-center gap-1.5 text-[11px] text-n-teal-11 font-semibold"
        >
          <i class="i-lucide-check-circle text-n-teal-10 shrink-0" />
          <span>Order placed{{ funnelDetail ? ' ' + funnelDetail : '' }}</span>
        </div>
        <div
          v-if="visitorDraft"
          class="flex items-center gap-1 text-[10px] text-n-slate-10"
        >
          <i class="i-lucide-pencil-line" />
          <span class="italic truncate">Typing...</span>
        </div>
        <div
          v-if="awarenessFlag"
          class="flex items-center gap-1.5 text-[11px] text-n-amber-11 font-medium"
        >
          <i class="i-lucide-activity text-n-amber-10 shrink-0" />
          <span class="truncate">{{ awarenessFlag }}</span>
        </div>
        <button
          v-if="overrideOfferable && !overrideDone"
          class="w-full flex items-center justify-center gap-1.5 rounded-md bg-n-brand text-white text-xs font-semibold py-1.5 px-2 hover:opacity-90 transition-opacity"
          @click="openOverrideModal"
        >
          <i class="i-lucide-message-square-plus" />
          Bring {{ flagName }} into chat
        </button>
        <div
          v-else-if="overrideDone"
          class="flex items-center gap-1.5 text-[11px] text-n-teal-11 font-medium"
        >
          <i class="i-lucide-check-circle text-n-teal-10" />
          Brought into chat
        </div>
      </div>

      <!-- Section 8: RELATED CHATS -- guest may still have prior chats -->
      <div v-if="hasChats" class="space-y-1">
        <button
          class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-0.5 hover:text-n-slate-12 transition-colors"
          @click="chatsOpen = !chatsOpen"
        >
          <span>Related Chats</span>
          <span
            class="flex items-center gap-1 normal-case font-normal text-n-slate-9"
          >
            <span v-if="!chatsOpen">{{ data.chats.threads.length }} previous</span>
            <i
              :class="
                chatsOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
              "
            />
          </span>
        </button>
        <div v-if="chatsOpen" class="space-y-1 pt-1">
          <a
            v-for="thread in data.chats.threads"
            :key="thread.id"
            :href="thread.url"
            target="_blank"
            rel="noopener"
            class="block text-xs text-n-slate-11 hover:text-n-brand truncate py-0.5"
          >
            <span class="font-medium text-n-slate-12">#{{ thread.id }}</span>
            {{ fmtDate(thread.created_at) }} -
            {{ thread.snippet || '(no preview)' }}
          </a>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- Full customer intel (matched registered customer)             -->
    <!-- ============================================================ -->
    <div v-else-if="data && data.found" class="space-y-0">
      <!-- Phase 2 (A): IMMEDIATE ATTENTION -- what matters right now (top of rail) -->
      <div v-if="showImmediateAttention" :class="iaToneClass" class="px-4 pt-3 pb-2 space-y-1 border-b border-n-weak">
        <div class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10">Immediate Attention</div>
        <div v-if="iaWarning" class="text-[12px] font-semibold leading-snug" :class="isConflictingMatch ? 'text-n-ruby-11' : 'text-n-amber-11'">{{ iaWarning }}</div>
        <div v-if="iaNextAction" class="leading-snug" :class="iaWarning ? 'text-[11px] text-n-slate-11' : 'text-[12px] font-semibold text-n-slate-12'">{{ iaNextAction }}</div>
        <div v-if="iaMeta" class="text-[11px] text-n-slate-10">{{ iaMeta }}</div>
      </div>
      <!-- == Section 1: IDENTITY + VALUE (CustomerSnapshotPanel #69 anchor) ==
           name/email (Chatwoot contact) + group/tenure (Magento) + spend/orders + cart dot + vehicle
           chip. Confidence stays in Immediate Attention (one focal). -->
      <div class="px-4 py-3">
        <CustomerSnapshotPanel
          :customer="snapshotCustomer"
          :stats="data.stats"
        />
      </div>

      <div class="border-t border-n-weak" />

      <!-- == Section 4: ACTIVE CART (only if real cart data exists) == -->
      <template v-if="hasCart">
        <div class="px-4 py-3 space-y-2">
          <div class="flex items-center justify-between">
            <div
              class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
            >
              Active Cart
              <span class="normal-case font-normal text-n-slate-9 ml-1">
                {{ data.cart.count }} item{{ data.cart.count !== 1 ? 's' : '' }}
              </span>
            </div>
            <span class="text-sm font-semibold text-n-slate-12">{{
              data.cart.formatted_total
            }}</span>
          </div>
          <div class="space-y-0.5">
            <div
              v-for="item in visibleCartItems"
              :key="item.sku"
              class="flex justify-between items-baseline text-xs py-0.5"
            >
              <span class="text-n-slate-11 truncate max-w-[60%]">{{
                item.name
              }}</span>
              <span class="text-n-slate-10 whitespace-nowrap ml-2 shrink-0">
                x{{ item.qty }} {{ item.formatted_row_total }}
              </span>
            </div>
            <button
              v-if="hiddenCartCount > 0 && !cartExpanded"
              class="text-[10px] text-n-slate-10 hover:text-n-brand mt-0.5"
              @click="cartExpanded = true"
            >
              + {{ hiddenCartCount }} more item{{
                hiddenCartCount !== 1 ? 's' : ''
              }}
            </button>
          </div>
        </div>
        <div class="border-t border-n-weak" />
      </template>

      <!-- == Section 5: CURRENT ORDER (one row, newest / linked) == -->
      <template v-if="currentOrder">
        <div class="px-4 py-3">
          <div
            class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 mb-1.5"
          >
            Current Order
          </div>

          <!-- C1: CONFLICTING MATCH banner (shown above the order row) -->
          <div
            v-if="isConflictingMatch"
            class="mb-2 px-2 py-1.5 rounded-md bg-n-ruby-5 border border-n-ruby-7 text-[11px] text-n-ruby-12 font-medium flex items-start gap-1.5"
          >
            <i class="i-lucide-alert-triangle text-n-ruby-11 shrink-0 mt-0.5" />
            <span>Order #{{ manualOrderNumber }} belongs to a different email —
              review before discussing order details</span>
          </div>

          <button
            class="w-full flex items-center justify-between text-xs py-1 rounded hover:bg-n-solid-2 transition-colors text-left group"
            @click="fetchOrderDetail(currentOrder.increment_id)"
          >
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="font-semibold text-n-slate-12">#{{ currentOrder.increment_id }}</span>
              <span
                :class="orderStateClass(currentOrder.state)"
                class="px-1.5 py-0.5 rounded text-[10px] capitalize font-medium"
              >
                {{ orderStateLabel(currentOrder.state, currentOrder.status) }}
              </span>
            </div>
            <div class="flex items-center gap-1.5 shrink-0 ml-2">
              <span class="text-n-slate-10 text-[11px]">{{
                fmtDate(currentOrder.created_at)
              }}</span>
              <span class="font-semibold text-n-slate-12">{{
                currentOrder.formatted_total
              }}</span>
              <i
                class="i-lucide-chevron-right text-n-slate-9 group-hover:text-n-brand"
              />
            </div>
          </button>

          <!-- C1: Order match result for manual order number -->
          <template v-if="manualOrderNumber">
            <div class="mt-1.5 flex items-center gap-1.5">
              <span class="text-[10px] text-n-slate-9">Manual order #{{ manualOrderNumber }}</span>
              <span
                v-if="orderMatchLoading"
                class="flex items-center gap-1 text-[10px] text-n-slate-10"
              >
                <Spinner size="12" class="text-n-slate-10" />
                Checking...
              </span>
              <span
                v-else-if="orderMatchResult && orderMatchResult.match_tier"
                :class="orderMatchTierClass"
                class="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide"
                >{{ orderMatchTierLabel }}</span>
              <span
                v-else-if="orderMatchError"
                class="text-[10px] text-n-ruby-11"
                >{{ orderMatchError }}</span>
            </div>
            <!-- C1: Evidence + conflicts (non-conflict only shows evidence; conflict shows masked_meta) -->
            <div
              v-if="
                orderMatchResult &&
                !isConflictingMatch &&
                orderMatchResult.evidence?.length
              "
              class="mt-1 space-y-0.5"
            >
              <div
                v-for="(ev, ei) in orderMatchResult.evidence"
                :key="ei"
                class="flex items-center gap-1 text-[10px] text-n-slate-10"
              >
                <i class="i-lucide-check text-n-teal-10 shrink-0" />
                <span>{{ ev }}</span>
              </div>
            </div>
            <div
              v-if="isConflictingMatch && orderMatchResult.masked_meta"
              class="mt-1 text-[10px] text-n-slate-10 space-y-0.5"
            >
              <div
                v-if="orderMatchResult.masked_meta.email_domain"
                class="flex justify-between"
              >
                <span>Email domain</span>
                <span class="font-medium">{{
                  orderMatchResult.masked_meta.email_domain
                }}</span>
              </div>
              <div
                v-if="orderMatchResult.masked_meta.name_initials"
                class="flex justify-between"
              >
                <span>Name initials</span>
                <span class="font-medium">{{
                  orderMatchResult.masked_meta.name_initials
                }}</span>
              </div>
            </div>
          </template>
        </div>
        <div class="border-t border-n-weak" />
      </template>

      <!-- == ENH-5: Suggested Responses (click-to-insert canned-response chips) == -->
      <template v-if="hasSuggestedMacros">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5 space-y-1.5">
          <div
            class="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            <i
              class="i-lucide-sparkles text-n-slate-9 shrink-0 text-[12px]"
            />
            <span>Suggested Responses</span>
          </div>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="macro in suggestedMacrosCapped"
              :key="macro.short_code"
              class="mvp-macro-chip group flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-60"
              :disabled="macroInsertBusy === macro.short_code"
              :title="macro.reason || macro.short_code"
              @click="insertMacro(macro)"
            >
              <Spinner
                v-if="macroInsertBusy === macro.short_code"
                size="10"
                class="text-n-slate-10"
              />
              <i
                v-else
                class="i-lucide-plus text-[11px] text-n-slate-9 shrink-0"
              />
              <span class="capitalize">{{
                macro.short_code.replace(/[-_]/g, ' ')
              }}</span>
            </button>
          </div>
          <div v-if="suggestedMacroOverflow > 0" class="text-[10px] text-n-slate-10">
            +{{ suggestedMacroOverflow }} more in the Customer Context tab
          </div>
          <div v-if="macroInsertError" class="text-[10px] text-n-ruby-11">
            {{ macroInsertError }}
          </div>
        </div>
      </template>

      <!-- == Section 6: ORDER HISTORY (collapsed, summary in header) == -->
      <template v-if="historyOrders.length > 0">
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="historyOpen = !historyOpen"
          >
            <span>Order History</span>
            <span
              class="flex items-center gap-1 normal-case font-normal text-n-slate-9"
            >
              <span v-if="!historyOpen" class="text-[10px]">{{
                historyHeaderSummary
              }}</span>
              <i
                :class="
                  historyOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
                "
              />
            </span>
          </button>
          <div v-if="historyOpen" class="space-y-0.5 mt-2">
            <button
              v-for="order in historyOrders"
              :key="order.increment_id"
              class="w-full flex items-center justify-between text-xs py-1 rounded hover:bg-n-solid-2 transition-colors text-left group"
              @click="fetchOrderDetail(order.increment_id)"
            >
              <div class="flex items-center gap-1.5 min-w-0">
                <span class="font-medium text-n-slate-12">#{{ order.increment_id }}</span>
                <span
                  :class="orderStateClass(order.state)"
                  class="px-1.5 py-0.5 rounded text-[10px] capitalize font-medium"
                >
                  {{ orderStateLabel(order.state, order.status) }}
                </span>
              </div>
              <div class="flex items-center gap-1.5 shrink-0 ml-2">
                <span class="text-n-slate-10 text-[11px]">{{
                  fmtDate(order.created_at)
                }}</span>
                <span class="font-medium text-n-slate-12">{{
                  order.formatted_total
                }}</span>
                <i
                  class="i-lucide-chevron-right text-n-slate-9 group-hover:text-n-brand"
                />
              </div>
            </button>
          </div>
        </div>
        <div class="border-t border-n-solid-3" />
      </template>

      <!-- == Section 2: VEHICLE / FITMENT == -->
      <!-- C3: Hide "Vehicle unknown" text; show section only when data is present -->
      <!-- VehicleFitmentContextPanel (#69 item 5 / I13 fix): source-labeled + confidence per vehicle
           (intake / garage / storefront), replacing the plain unlabeled row. Uses existing vehicleContext. -->
      <div v-if="hasVehicleContext" class="px-4 py-3 space-y-1.5">
        <div
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          Vehicle
        </div>
        <VehicleFitmentContextPanel :vehicle-context="vehicleContext" />
      </div>
      <!-- C2: Journey / Current Page -- show here near top when we have page data but no vehicle -->
      <div v-else-if="hasJourneyData" class="px-4 py-3 space-y-1.5">
        <div
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          Current Page
        </div>
        <a
          v-if="currentPageUrl"
          :href="currentPageUrl"
          target="_blank"
          rel="noopener"
          class="block text-xs text-n-brand hover:underline truncate"
          :title="currentPageUrl"
          >{{ currentPageTitle || currentPageUrl }}</a>
        <div v-if="recentPages.length > 0" class="space-y-0.5 pt-0.5">
          <div class="text-[10px] text-n-slate-9 uppercase tracking-wide">
            Recent
          </div>
          <a
            v-for="(page, pi) in recentPages"
            :key="pi"
            :href="page.url"
            target="_blank"
            rel="noopener"
            class="block text-[11px] text-n-slate-11 hover:text-n-brand truncate py-0.5"
            :title="page.url"
            >{{ page.title || page.url }}</a>
        </div>
      </div>

      <!-- C2: Also show journey data after Vehicle section if vehicle is known -->
      <template v-if="intakeVehicle && hasJourneyData">
        <div class="border-t border-n-weak" />
        <div class="px-4 py-3 space-y-1.5">
          <div
            class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            Current Page
          </div>
          <a
            v-if="currentPageUrl"
            :href="currentPageUrl"
            target="_blank"
            rel="noopener"
            class="block text-xs text-n-brand hover:underline truncate"
            :title="currentPageUrl"
            >{{ currentPageTitle || currentPageUrl }}</a>
          <div v-if="recentPages.length > 0" class="space-y-0.5 pt-0.5">
            <div class="text-[10px] text-n-slate-9 uppercase tracking-wide">
              Recent
            </div>
            <a
              v-for="(page, pi) in recentPages"
              :key="pi"
              :href="page.url"
              target="_blank"
              rel="noopener"
              class="block text-[11px] text-n-slate-11 hover:text-n-brand truncate py-0.5"
              :title="page.url"
              >{{ page.title || page.url }}</a>
          </div>
        </div>
      </template>

      <div class="border-t border-n-weak" />

      <!-- == Agent Actions: set catalog price / make coupon / add product from URL (#69 item 9) == -->
      <AgentShoppingActions
        :email="contactEmail"
        :conversation-id="conversationId"
        :products="shopProducts"
        :behavior="data?.behavior"
        :allowed-writes="data?.allowed_writes"
      />

      <!-- == Section 3: LIVE CONTEXT (ONE amber accent area) == -->
      <!-- Visible when funnel is active, awareness flag is set, visitor is typing, or override available -->
      <div
        v-if="
          funnelInCheckout ||
          funnelOrderPlaced ||
          awarenessFlag ||
          visitorDraft ||
          overrideOfferable ||
          overrideDone ||
          routingGroup ||
          (hasLastSeen && !funnelOrderPlaced)
        "
        class="px-4 py-3 space-y-2 border-b border-n-weak"
      >
        <div
          class="text-[10px] font-semibold uppercase tracking-wide text-n-amber-10"
        >
          Live
        </div>

        <!-- Primary funnel status -->
        <div
          v-if="funnelInCheckout"
          class="flex items-center gap-1.5 text-[11px] text-n-amber-11 font-semibold"
        >
          <i class="i-lucide-shopping-cart text-n-amber-10 shrink-0" />
          <span>In checkout now{{ funnelDetail ? ' - ' + funnelDetail : '' }}</span>
        </div>
        <div
          v-else-if="funnelOrderPlaced"
          class="flex items-center gap-1.5 text-[11px] text-n-teal-11 font-semibold"
        >
          <i class="i-lucide-check-circle text-n-teal-10 shrink-0" />
          <span>Order placed{{ funnelDetail ? ' ' + funnelDetail : '' }}</span>
        </div>

        <!-- Last active (secondary, when no strong funnel signal) -->
        <div
          v-if="!funnelInCheckout && !funnelOrderPlaced && hasLastSeen"
          class="text-[11px] text-n-amber-10"
        >
          Last active {{ fmtRelative(data.behavior.last_seen) }}
        </div>

        <!-- Visitor typing -->
        <div
          v-if="visitorDraft"
          class="flex items-center gap-1 text-[10px] text-n-amber-10"
        >
          <i class="i-lucide-pencil-line shrink-0" />
          <span class="italic truncate">Typing...</span>
        </div>

        <!-- Agent-awareness flag from intake state machine -->
        <div
          v-if="awarenessFlag"
          class="flex items-center gap-1.5 text-[11px] text-n-amber-11 font-medium"
        >
          <i class="i-lucide-activity text-n-amber-10 shrink-0" />
          <span class="truncate">{{ awarenessFlag }}</span>
        </div>

        <!-- ENH-1: Skill-based routing badge + "Wrong team?" relabel (#17 sapphire accent) -->
        <div v-if="routingGroup" class="space-y-1 pt-0.5">
          <div class="flex items-center gap-1.5 flex-wrap">
            <i class="i-lucide-route text-n-slate-9 shrink-0 text-[12px]" />
            <span
              class="mvp-routing-badge px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize whitespace-nowrap"
              >{{ routingGroupLabel }}</span>
            <ConfidenceBadge
              :level="routingConfidenceLevel"
              :reason="routingReason"
            />
            <button
              v-if="relabelConfigured"
              class="mvp-sapphire-link ml-auto text-[10px] text-n-slate-10 underline decoration-dotted transition-colors shrink-0"
              @click="openRelabel"
            >
              Wrong team?
            </button>
            <span
              v-else
              class="ml-auto text-[10px] text-n-slate-9 italic shrink-0"
              title="Relabeling is not enabled for this store (override token not configured)."
            >
              Relabel not enabled
            </span>
          </div>
          <div
            v-if="routingReason"
            class="text-[10px] text-n-slate-10 leading-snug pl-5"
          >
            {{ routingReason }}
          </div>

          <!-- Relabel inline control -->
          <div v-if="relabelOpen" class="pl-5 space-y-1.5 pt-1">
            <div class="flex items-center gap-1.5">
              <select
                v-model="relabelGroup"
                class="mvp-relabel-select flex-1 text-[11px] rounded border border-n-amber-5 bg-n-background text-n-slate-12 px-1.5 py-1 focus:outline-none"
              >
                <option value="" disabled>Reassign to team…</option>
                <option
                  v-for="g in RELABEL_GROUPS"
                  :key="g.slug"
                  :value="g.slug"
                >
                  {{ g.label }}
                </option>
              </select>
              <button
                class="mvp-sapphire-btn text-[11px] font-semibold rounded-md text-white px-2 py-1 transition-colors disabled:opacity-50"
                :disabled="relabelBusy || !relabelGroup"
                @click="submitRelabel"
              >
                {{ relabelBusy ? 'Saving…' : 'Relabel' }}
              </button>
            </div>
            <div
              v-if="relabelResult && relabelResult !== 'ok'"
              class="text-[10px] text-n-ruby-11"
            >
              {{ relabelResult }}
            </div>
          </div>
          <div
            v-else-if="relabelResult === 'ok'"
            class="pl-5 flex items-center gap-1 text-[10px] text-n-teal-11 font-medium"
          >
            <i class="i-lucide-check-circle text-n-teal-10" />
            Reassigned
          </div>
        </div>

        <!-- Override / bring-into-chat action co-located -->
        <button
          v-if="overrideOfferable && !overrideDone"
          class="w-full flex items-center justify-center gap-1.5 rounded-md bg-n-brand text-white text-xs font-semibold py-1.5 px-2 hover:opacity-90 transition-opacity"
          @click="openOverrideModal"
        >
          <i class="i-lucide-message-square-plus" />
          Bring {{ flagName }} into chat
        </button>
        <div
          v-else-if="overrideDone"
          class="flex items-center gap-1.5 text-[11px] text-n-teal-11 font-medium"
        >
          <i class="i-lucide-check-circle text-n-teal-10" />
          Brought into chat (details preserved)
        </div>
        <div
          v-if="overrideResult && overrideResult !== 'ok'"
          class="text-[10px] text-n-ruby-11"
        >
          {{ overrideResult }}
        </div>
      </div>

      <!-- == Section 7: BEHAVIOR (collapsed; replaces the 2nd amber card) == -->
      <template v-if="hasUpsell || hasActivity">
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="behaviorOpen = !behaviorOpen"
          >
            <span>Behavior</span>
            <span
              class="flex items-center gap-1 normal-case font-normal text-n-slate-9"
            >
              <span
                v-if="!behaviorOpen && behaviorHeaderSummary"
                class="text-[10px] truncate max-w-[100px]"
              >
                {{ behaviorHeaderSummary }}
              </span>
              <i
                :class="
                  behaviorOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
                "
              />
            </span>
          </button>
          <div v-if="behaviorOpen" class="space-y-3 mt-2">
            <!-- Repeat views / upsell signal -->
            <div v-if="hasUpsell" class="space-y-0.5">
              <div
                class="text-[10px] uppercase tracking-wide text-n-slate-9 mb-1"
              >
                Viewed more than once
              </div>
              <div
                v-for="(pv, i) in (data.behavior.repeat_views || []).slice(
                  0,
                  6
                )"
                :key="i"
                class="flex justify-between items-baseline text-xs py-0.5"
              >
                <span class="flex items-center gap-1 max-w-[72%]">
                  <a
                    v-if="pv.url"
                    :href="pv.url"
                    target="_blank"
                    rel="noopener"
                    class="text-n-slate-11 hover:text-n-brand truncate"
                    >{{ pv.name }}</a>
                  <span v-else class="text-n-slate-11 truncate">{{
                    pv.name
                  }}</span>
                  <!-- #50 inventory-awareness: out-of-stock badge (renders only when explicitly false) -->
                  <span
                    v-if="pv.in_stock === false"
                    class="shrink-0 text-[9px] font-semibold uppercase px-1 py-px rounded bg-n-ruby-3 text-n-ruby-11"
                    title="This product is currently out of stock"
                    >Out of stock</span>
                </span>
                <span class="text-n-slate-10 ml-2 shrink-0 whitespace-nowrap">
                  {{ pv.view_count || pv.views || 0 }}x
                  <span v-if="pv.formatted_price" class="ml-1">{{
                    pv.formatted_price
                  }}</span>
                </span>
              </div>
              <!-- Bring top product into chat -->
              <div v-if="topUpsell && topUpsell.url" class="pt-1">
                <a
                  :href="topUpsell.url"
                  target="_blank"
                  rel="noopener"
                  class="text-[10px] text-n-brand hover:underline"
                >
                  Open
                  {{
                    topUpsell.name
                      ? topUpsell.name.slice(0, 28) +
                        (topUpsell.name.length > 28 ? '...' : '')
                      : 'product'
                  }}
                </a>
              </div>
            </div>

            <!-- Recent activity events -->
            <!-- C3: Filter out page_view events already shown in Journey; show only non-trivial actions -->
            <div v-if="hasActivity" class="space-y-0.5">
              <div
                class="text-[10px] uppercase tracking-wide text-n-slate-9 mb-1"
              >
                Recent activity
              </div>
              <template
                v-for="(activity, i) in (
                  data.behavior.recent_activity || []
                ).slice(0, 6)"
                :key="i"
              >
                <div
                  v-if="activity.action || activity.type"
                  class="text-xs text-n-slate-11 py-0.5"
                >
                  <span class="text-n-slate-12">{{
                    activity.action || activity.type
                  }}</span>
                  <span v-if="activity.target_name"
class="ml-1"
                    >- {{ activity.target_name }}</span>
                  <span v-if="activity.created_at"
class="text-n-slate-9 ml-1"
                    >({{ fmtRelative(activity.created_at) }})</span>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="border-t border-n-solid-3" />
      </template>

      <!-- == Section 8: RELATED CHATS (collapsed) == -->
      <template v-if="hasChats">
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="chatsOpen = !chatsOpen"
          >
            <span>Related Chats</span>
            <span
              class="flex items-center gap-1 normal-case font-normal text-n-slate-9"
            >
              <span v-if="!chatsOpen">{{ data.chats.threads.length }} previous</span>
              <i
                :class="
                  chatsOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
                "
              />
            </span>
          </button>
          <div v-if="chatsOpen" class="space-y-1 mt-2">
            <a
              v-for="thread in data.chats.threads"
              :key="thread.id"
              :href="thread.url"
              target="_blank"
              rel="noopener"
              class="block text-xs text-n-slate-11 hover:text-n-brand py-0.5 truncate"
            >
              <span class="text-n-slate-12 font-medium">#{{ thread.id }}</span>
              {{ fmtDate(thread.created_at) }} -
              {{ thread.snippet || '(no preview)' }}
            </a>
          </div>
        </div>
      </template>

      <!-- == ENH-2: Triage summary -- bold header row (section 9) == -->
      <template v-if="triageSummary">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <div
            class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 mb-1"
          >
            Triage
          </div>
          <div
            class="mvp-triage-summary flex items-start gap-1.5 text-xs font-semibold text-n-slate-12 leading-snug rounded-md px-2 py-1.5"
          >
            <i
              class="i-lucide-clipboard-list text-n-slate-9 shrink-0 mt-px text-[13px]"
            />
            <span>{{ triageSummary }}</span>
          </div>
        </div>
      </template>

      <!-- == Section 9: PRE-CHAT LEAD detail (only if structured data beyond awareness) == -->
      <template v-if="intakeActive && leadHasStructuredDetail">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="leadDetailOpen = !leadDetailOpen"
          >
            <span>Pre-Chat Lead</span>
            <span
              class="flex items-center gap-1 normal-case font-normal text-n-slate-9"
            >
              <span v-if="!leadDetailOpen">
                {{ leadRequiredComplete ? 'complete' : 'partial' }} ({{
                  leadFieldCount
                }}/4)
              </span>
              <i
                :class="
                  leadDetailOpen
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
              />
            </span>
          </button>
          <div v-if="leadDetailOpen" class="space-y-0.5 mt-2">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[10px] text-n-slate-10">Stage</span>
              <span
                class="px-1.5 py-0.5 rounded text-[10px] font-medium bg-n-brand/10 text-n-brand capitalize"
              >
                {{ stageLabel }}
              </span>
            </div>
            <div v-if="leadName" class="flex justify-between text-xs py-0.5">
              <span class="text-n-slate-10">Name</span>
              <span class="text-n-slate-12 font-medium truncate max-w-[60%]">{{
                leadName
              }}</span>
            </div>
            <div v-if="leadEmail" class="flex justify-between text-xs py-0.5">
              <span class="text-n-slate-10">Email</span>
              <span class="text-n-slate-12 font-medium truncate max-w-[60%]">{{
                leadEmail
              }}</span>
            </div>
            <div v-if="leadPhone" class="flex justify-between text-xs py-0.5">
              <span class="text-n-slate-10">Phone</span>
              <span class="text-n-slate-12 font-medium">{{ leadPhone }}</span>
            </div>
            <div v-if="leadOrder" class="flex justify-between text-xs py-0.5">
              <span class="text-n-slate-10">Order #</span>
              <span class="text-n-slate-12 font-medium">#{{ leadOrder }}</span>
            </div>
            <div
              v-if="intakeVehicle || intakePartName"
              class="space-y-0.5 pt-1.5 border-t border-n-slate-5 mt-1"
            >
              <div
                v-if="intakeVehicle"
                class="flex justify-between text-xs py-0.5"
              >
                <span class="text-n-slate-10 flex items-center gap-1">
                  <i class="i-lucide-car text-n-slate-9" />Vehicle
                </span>
                <span
                  class="text-n-slate-12 font-medium truncate max-w-[65%]"
                  >{{ intakeVehicle }}</span>
              </div>
              <div
                v-if="intakePartName"
                class="flex justify-between text-xs py-0.5"
              >
                <span class="text-n-slate-10 flex items-center gap-1">
                  <i class="i-lucide-package text-n-slate-9" />Part
                </span>
                <span class="text-n-slate-12 font-medium truncate max-w-[65%]">
                  {{ intakePartName
                  }}<span
                    v-if="intakePartNumber"
                    class="text-n-slate-10 font-normal"
                  >
                    ({{ intakePartNumber }})</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- == ENH-4: PENDING ESCALATIONS section == -->
      <template v-if="escalationsData !== null || hasOpenEscalations">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="escalationsOpen = !escalationsOpen"
          >
            <span class="flex items-center gap-1.5">
              <span>Pending Escalations</span>
              <span
                v-if="hasOpenEscalations"
                class="px-1.5 py-0.5 rounded-full bg-n-ruby-5 text-n-ruby-12 text-[9px] font-bold"
                >{{ openEscalations.length }}</span>
            </span>
            <span class="flex items-center gap-1">
              <span
                v-if="!escalationsOpen && !hasOpenEscalations"
                class="text-[10px] text-n-slate-9"
                >none</span>
              <i
                :class="
                  escalationsOpen
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
              />
            </span>
          </button>
          <div v-if="escalationsOpen" class="mt-2 space-y-1">
            <div
              v-if="escalationsLoading"
              class="flex items-center gap-1.5 text-[11px] text-n-slate-10"
            >
              <Spinner size="12" class="text-n-slate-10" />
              <span>Loading...</span>
            </div>
            <CompactEmptyState
              v-else-if="!hasOpenEscalations"
              label="No open escalations."
            />
            <template v-else>
              <div
                v-for="esc in openEscalations"
                :key="esc.escalation_id"
                class="flex items-center justify-between text-xs py-0.5 gap-2"
              >
                <div class="flex items-center gap-1.5 min-w-0">
                  <span
                    class="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-n-amber-5 text-n-amber-12 shrink-0"
                    >{{
                      (esc.type || esc.escalation_type || '').replace(/_/g, ' ')
                    }}</span>
                  <span class="text-n-slate-11 truncate">{{
                    esc.agent_note || ''
                  }}</span>
                </div>
                <span class="text-[10px] text-n-slate-9 shrink-0">{{
                  fmtRelative(esc.created_at)
                }}</span>
              </div>
            </template>
          </div>
        </div>
      </template>

      <!-- == ENH-6: RMA / Support Topic badge == -->
      <template v-if="supportTopic">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <div
            class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 mb-1.5"
          >
            Support Topic
          </div>
          <div class="flex items-center gap-1.5 flex-wrap">
            <span
              class="px-2 py-1 rounded-md text-xs font-medium bg-n-slate-3 text-n-slate-11 capitalize"
            >
              {{ supportTopicLabel }}
            </span>
            <ConfidenceBadge level="Confirmed" reason="customer declared" />
            <span
              v-if="supportTopicOrderVerified"
              class="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-n-slate-3 text-n-slate-11 uppercase tracking-wide"
            >
              Order Verified
            </span>
          </div>
        </div>
      </template>

      <!-- == ENH-8: QA Outcome chips (config-gated; only when conversation resolved) == -->
      <template v-if="qaEnabled && convResolved">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="qaOutcomeOpen = !qaOutcomeOpen"
          >
            <span>QA Outcome</span>
            <i
              :class="
                qaOutcomeOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
              "
            />
          </button>
          <div v-if="qaOutcomeOpen" class="mt-2 space-y-2">
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="item in QA_OUTCOME_CODES"
                :key="item.code"
                :class="
                  qaOutcomeCodes.includes(item.code)
                    ? 'bg-n-brand text-white'
                    : 'bg-n-solid-3 text-n-slate-11 hover:bg-n-solid-4'
                "
                class="px-2 py-1 rounded text-[10px] font-medium transition-colors"
                @click="
                  qaOutcomeCodes.includes(item.code)
                    ? qaOutcomeCodes.splice(
                        qaOutcomeCodes.indexOf(item.code),
                        1
                      )
                    : qaOutcomeCodes.push(item.code)
                "
              >
                {{ item.label }}
              </button>
            </div>
            <button
              v-if="qaOutcomeCodes.length > 0"
              class="w-full rounded-md bg-n-teal-5 text-n-teal-12 text-xs font-semibold py-1.5 hover:bg-n-teal-6 transition-colors"
              @click="submitQaOutcome"
            >
              Submit Outcome ({{ qaOutcomeCodes.length }} code{{
                qaOutcomeCodes.length !== 1 ? 's' : ''
              }})
            </button>
            <CompactEmptyState
              v-else
              label="Select outcome codes above to record."
            />
          </div>
        </div>
      </template>
    </div>

    <!-- ============================================================ -->
    <!-- Phase 4: BRIDGE SECTIONS (only when data.bridge present)     -->
    <!-- ============================================================ -->
    <template v-if="data && data.bridge">
      <!-- == Bridge: Identity Confidence + Confirm/Override == -->
      <!-- change 1: full block (evidence + actions) only for medium/weak; verified/strong show only the header badge already present above -->
      <template
        v-if="
          bridgeIdentity &&
          (bridgeIdentity.confidence === 'medium' ||
            bridgeIdentity.confidence === 'weak')
        "
      >
        <div class="border-t border-n-weak" />
        <div class="px-4 py-3 space-y-2">
          <div
            class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            Identity Confidence
          </div>
          <!-- Evidence list -->
          <div
            v-if="bridgeIdentity.evidence && bridgeIdentity.evidence.length"
            class="space-y-0.5"
          >
            <div
              v-for="(ev, i) in bridgeIdentity.evidence"
              :key="i"
              class="flex items-center gap-1 text-[11px] text-n-slate-11"
            >
              <i class="i-lucide-check-circle text-n-teal-10 shrink-0" />
              <span>{{ ev }}</span>
            </div>
          </div>
          <!-- C3: Only show "No identity evidence" if evidence array is explicitly empty (not missing) -->
          <div
            v-else-if="bridgeIdentity.evidence !== undefined"
            class="text-xs text-n-slate-10 italic"
          >
            No identity evidence available
          </div>
          <!-- change 1 + change 2: Confirm/Override buttons only for medium/weak; Override is a quiet ghost (outline only) -->
          <div
            v-if="
              confirmOfferable &&
              !bridgeConfirmBusy &&
              bridgeConfirmResult !== 'ok'
            "
            class="flex gap-2 pt-1"
          >
            <button
              class="flex-1 flex items-center justify-center gap-1 rounded-md bg-n-teal-5 text-n-teal-12 text-[11px] font-semibold py-1.5 hover:bg-n-teal-6 transition-colors"
              @click="submitIdentityConfirm('confirm')"
            >
              <i class="i-lucide-check" />
              Confirm
            </button>
            <!-- change 2: ghost/outline style (border + neutral text, no solid fill) so Confirm is the single primary action -->
            <button
              class="flex-1 flex items-center justify-center gap-1 rounded-md border border-n-weak text-n-slate-11 text-[11px] font-medium py-1.5 hover:border-n-slate-8 hover:text-n-slate-12 transition-colors"
              @click="submitIdentityConfirm('override')"
            >
              <i class="i-lucide-user-check" />
              Override
            </button>
          </div>
          <div
            v-if="bridgeConfirmBusy"
            class="flex items-center gap-1.5 text-[11px] text-n-slate-11"
          >
            <Spinner size="14" class="text-n-brand" />
            <span>Recording...</span>
          </div>
          <div
            v-if="bridgeConfirmResult === 'ok'"
            class="flex items-center gap-1.5 text-[11px] text-n-teal-11"
          >
            <i class="i-lucide-check-circle text-n-teal-10" />
            <span>Identity confirmation recorded</span>
          </div>
          <div
            v-else-if="bridgeConfirmResult"
            class="text-[11px] text-n-ruby-11"
          >
            {{ bridgeConfirmResult }}
          </div>
        </div>
      </template>

      <!-- == Bridge: Garage Vehicles == -->
      <template v-if="bridgeGarage.length > 0">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="bridgeGarageOpen = !bridgeGarageOpen"
          >
            <span>Garage ({{ bridgeGarage.length }})</span>
            <i
              :class="
                bridgeGarageOpen
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
            />
          </button>
          <div v-if="bridgeGarageOpen" class="space-y-1 mt-2">
            <div
              v-for="(v, i) in bridgeGarage"
              :key="i"
              class="flex items-center gap-1.5 text-xs py-0.5"
            >
              <i class="i-lucide-car text-n-slate-9 shrink-0" />
              <span class="text-n-slate-12 font-medium">{{ v.year }} {{ v.make }} {{ v.model }}</span>
              <span
                v-if="v.submodel && v.submodel !== 'All'"
                class="text-n-slate-10 truncate"
                >{{ v.submodel }}</span>
              <span
                v-if="v.is_default"
                class="ml-auto text-[9px] bg-n-brand/10 text-n-brand px-1 py-0.5 rounded font-semibold"
                >default</span>
            </div>
          </div>
        </div>
      </template>

      <!-- == Bridge: Recently Viewed == -->
      <template v-if="bridgeRecentlyViewed.length > 0">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="bridgeRecentOpen = !bridgeRecentOpen"
          >
            <span>Recently Viewed</span>
            <span
              class="flex items-center gap-1 normal-case font-normal text-n-slate-9"
            >
              <span v-if="!bridgeRecentOpen">{{
                bridgeRecentlyViewed.length
              }}</span>
              <i
                :class="
                  bridgeRecentOpen
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
              />
            </span>
          </button>
          <div v-if="bridgeRecentOpen" class="space-y-0.5 mt-2">
            <div
              v-for="(item, i) in bridgeRecentlyViewed.slice(0, 8)"
              :key="i"
              class="flex items-baseline justify-between text-xs py-0.5"
            >
              <a
                v-if="item.url"
                :href="item.url"
                target="_blank"
                rel="noopener"
                class="text-n-slate-11 hover:text-n-brand truncate max-w-[72%]"
                >{{ item.name || item.sku }}</a>
              <span v-else class="text-n-slate-11 truncate max-w-[72%]">{{
                item.name || item.sku
              }}</span>
              <span
                v-if="item.formatted_price"
                class="text-n-slate-10 ml-2 shrink-0"
                >{{ item.formatted_price }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- == Bridge: Saved For Later == -->
      <template v-if="bridgeSavedForLater.length > 0">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="bridgeSavedOpen = !bridgeSavedOpen"
          >
            <span>Saved for Later</span>
            <span
              class="flex items-center gap-1 normal-case font-normal text-n-slate-9"
            >
              <span v-if="!bridgeSavedOpen">{{
                bridgeSavedForLater.length
              }}</span>
              <i
                :class="
                  bridgeSavedOpen
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
              />
            </span>
          </button>
          <div v-if="bridgeSavedOpen" class="space-y-0.5 mt-2">
            <div
              v-for="(item, i) in bridgeSavedForLater.slice(0, 6)"
              :key="i"
              class="flex items-baseline justify-between text-xs py-0.5"
            >
              <span class="text-n-slate-11 truncate max-w-[72%]">{{
                item.name || item.sku
              }}</span>
              <div class="flex items-center gap-2 shrink-0 ml-2">
                <span v-if="item.qty && item.qty !== 1"
class="text-n-slate-10"
                  >x{{ item.qty }}</span>
                <span v-if="item.formatted_price" class="text-n-slate-10">{{
                  item.formatted_price
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- == Bridge: Ops Status == -->
      <template v-if="bridgeOpsStatus.length > 0">
        <div class="border-t border-n-solid-3" />
        <div class="px-4 py-2.5">
          <button
            class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 py-1 hover:text-n-slate-12 transition-colors"
            @click="bridgeOpsOpen = !bridgeOpsOpen"
          >
            <span>Ops Status</span>
            <i
              :class="
                bridgeOpsOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
              "
            />
          </button>
          <div v-if="bridgeOpsOpen" class="space-y-1.5 mt-2">
            <div
              v-for="(item, i) in bridgeOpsStatus"
              :key="i"
              class="flex items-center justify-between text-xs py-0.5"
            >
              <span class="text-n-slate-11">{{ item.label }}</span>
              <div class="flex items-center gap-1.5 shrink-0 ml-2">
                <span
                  v-if="item.status"
                  class="px-1.5 py-0.5 rounded text-[9px] font-medium capitalize bg-n-solid-3 text-n-slate-11"
                  >{{ item.status.replace(/_/g, ' ') }}</span>
                <span
                  v-if="item.ops && item.ops.stripe_status"
                  class="text-[10px] text-n-slate-10"
                >
                  Stripe: {{ item.ops.stripe_status }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- ============================================================ -->
    <!-- C4: DEBUG PANEL - engineer-only (debug flag); hidden from agents to keep the rail clean -->
    <!-- ============================================================ -->
    <template v-if="data && showDebugPanel">
      <div class="border-t border-n-solid-3" />
      <div class="px-4 py-2">
        <button
          class="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-n-slate-9 py-1 hover:text-n-slate-11 transition-colors"
          @click="debugOpen = !debugOpen"
        >
          <span class="flex items-center gap-1">
            <i class="i-lucide-code text-n-slate-8" />
            Raw data
          </span>
          <i
            :class="debugOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            class="text-n-slate-8"
          />
        </button>
        <div v-if="debugOpen" class="mt-2">
          <pre
            class="text-[10px] text-n-slate-10 font-mono bg-n-solid-2 rounded p-2 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all leading-relaxed"
            >{{ JSON.stringify(data, null, 2) }}</pre>
        </div>
      </div>
    </template>

    <!-- ============================================================ -->
    <!-- ORDER DETAIL MODAL (unchanged -- fixed overlay)              -->
    <!-- ============================================================ -->
    <div
      v-if="orderModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-bg-1/80 backdrop-blur-sm"
      @click.self="closeModal"
    >
      <div
        class="bg-n-bg-1 rounded-xl shadow-2xl border border-n-weak w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak sticky top-0 bg-n-bg-1 z-10"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            Order Detail
            <span
              v-if="orderDetail?.increment_id"
              class="text-n-slate-11 font-normal ml-1"
              >#{{ orderDetail.increment_id }}</span>
          </h3>
          <button
            class="text-n-slate-11 hover:text-n-slate-12"
            @click="closeModal"
          >
            <i class="i-lucide-x text-base" />
          </button>
        </div>

        <div v-if="orderLoading" class="flex items-center justify-center py-8">
          <Spinner size="32" class="text-n-brand" />
        </div>

        <div
          v-else-if="orderDetail?._error"
          class="px-4 py-4 text-xs text-n-ruby-11"
        >
          Failed to load: {{ orderDetail._error }}
        </div>

        <div
          v-else-if="!orderDetail"
          class="px-4 py-4 text-xs text-n-slate-11 text-center"
        >
          Order not found
        </div>

        <div v-else class="px-4 py-3 space-y-4 text-xs text-n-slate-12">
          <div class="flex items-center justify-between">
            <span
              :class="orderStateClass(orderDetail.state)"
              class="px-2 py-1 rounded text-[11px] font-medium capitalize"
            >
              {{ orderStateLabel(orderDetail.state, orderDetail.status) }}
            </span>
            <div class="text-n-slate-11">
              {{ fmtDate(orderDetail.created_at) }}
            </div>
          </div>

          <div v-if="orderDetail.items?.length">
            <div
              class="font-semibold text-n-slate-12 mb-1 uppercase tracking-wide text-[10px]"
            >
              Items
            </div>
            <div
              v-for="item in orderDetail.items"
              :key="item.sku"
              class="flex justify-between items-start py-1 border-b border-n-weak last:border-0"
            >
              <div class="flex-1 min-w-0 pr-2">
                <div class="font-medium text-n-slate-12 truncate">
                  {{ item.name }}
                </div>
                <div class="text-n-slate-11">{{ item.sku }}</div>
                <div class="text-n-slate-11">
                  Ordered: {{ item.qty_ordered }}
                  <span v-if="item.qty_shipped > 0"
class="ml-1 text-n-teal-11"
                    >Shipped: {{ item.qty_shipped }}</span>
                  <span v-if="item.qty_refunded > 0"
class="ml-1 text-n-ruby-11"
                    >Refunded: {{ item.qty_refunded }}</span>
                </div>
              </div>
              <div class="text-right shrink-0">
                <div class="font-medium">{{ item.formatted_row_total }}</div>
                <div class="text-n-slate-11">{{ item.formatted_price }} ea</div>
              </div>
            </div>
          </div>

          <div v-if="orderDetail.totals">
            <div
              class="font-semibold text-n-slate-12 mb-1 uppercase tracking-wide text-[10px]"
            >
              Totals
            </div>
            <div class="space-y-0.5">
              <div
                v-if="orderDetail.totals.subtotal"
                class="flex justify-between"
              >
                <span class="text-n-slate-11">Subtotal</span>
                <span>{{ orderDetail.totals.subtotal }}</span>
              </div>
              <div
                v-if="orderDetail.totals.shipping"
                class="flex justify-between"
              >
                <span class="text-n-slate-11">Shipping</span>
                <span>{{ orderDetail.totals.shipping }}</span>
              </div>
              <div
                v-if="
                  orderDetail.totals.discount &&
                  orderDetail.totals.discount !== '$0.00'
                "
                class="flex justify-between"
              >
                <span class="text-n-slate-11">Discount</span>
                <span class="text-n-teal-11">{{
                  orderDetail.totals.discount
                }}</span>
              </div>
              <div v-if="orderDetail.totals.tax" class="flex justify-between">
                <span class="text-n-slate-11">Tax</span>
                <span>{{ orderDetail.totals.tax }}</span>
              </div>
              <div
                class="flex justify-between font-semibold border-t border-n-weak pt-1 mt-1"
              >
                <span>Total</span>
                <span>{{ orderDetail.totals.grand_total }}</span>
              </div>
              <div
                v-if="
                  orderDetail.totals.total_paid &&
                  orderDetail.totals.total_paid !== '$0.00'
                "
                class="flex justify-between text-n-teal-11"
              >
                <span>Paid</span>
                <span>{{ orderDetail.totals.total_paid }}</span>
              </div>
              <div
                v-if="
                  orderDetail.totals.total_due &&
                  orderDetail.totals.total_due !== '$0.00'
                "
                class="flex justify-between text-n-amber-11"
              >
                <span>Due</span>
                <span>{{ orderDetail.totals.total_due }}</span>
              </div>
            </div>
          </div>

          <div v-if="orderDetail.payment">
            <div
              class="font-semibold text-n-slate-12 mb-1 uppercase tracking-wide text-[10px]"
            >
              Payment
            </div>
            <div class="space-y-0.5">
              <div class="flex justify-between">
                <span class="text-n-slate-11">Method</span>
                <span>{{
                  orderDetail.payment.method_title || orderDetail.payment.method
                }}</span>
              </div>
              <div
                v-if="orderDetail.payment.cc_last4"
                class="flex justify-between"
              >
                <span class="text-n-slate-11">Card</span>
                <span>{{ orderDetail.payment.cc_type }} ending
                  {{ orderDetail.payment.cc_last4 }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-n-slate-11">Status</span>
                <span class="capitalize">{{ orderDetail.payment.state }}</span>
              </div>
            </div>
          </div>

          <div v-if="orderDetail.shipments?.length">
            <div
              class="font-semibold text-n-slate-12 mb-1 uppercase tracking-wide text-[10px]"
            >
              Tracking
            </div>
            <div
              v-for="(ship, si) in orderDetail.shipments"
              :key="si"
              class="space-y-0.5"
            >
              <div
                v-for="(track, ti) in ship.tracks || []"
                :key="ti"
                class="flex justify-between"
              >
                <span class="text-n-slate-11">{{ track.carrier_title }}</span>
                <span class="font-medium">{{ track.number }}</span>
              </div>
            </div>
          </div>

          <div v-if="orderDetail.shipping_address">
            <div
              class="font-semibold text-n-slate-12 mb-1 uppercase tracking-wide text-[10px]"
            >
              Ships To
            </div>
            <div class="text-n-slate-11 leading-relaxed">
              {{ orderDetail.shipping_address.firstname }}
              {{ orderDetail.shipping_address.lastname }}<br />
              {{ orderDetail.shipping_address.street?.join(', ') }}<br />
              {{ orderDetail.shipping_address.city }},
              {{ orderDetail.shipping_address.region }}
              {{ orderDetail.shipping_address.postcode }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- AGENT OVERRIDE CONFIRM MODAL (#68 Phase 4, unchanged)        -->
  <!-- ============================================================ -->
  <div
    v-if="overrideModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-bg-1/80 backdrop-blur-sm"
    @click.self="closeOverrideModal"
  >
    <div
      class="bg-n-bg-1 rounded-xl shadow-2xl border border-n-weak w-full max-w-sm mx-4"
    >
      <div class="px-4 py-3 border-b border-n-weak">
        <h3 class="text-sm font-semibold text-n-slate-12">
          Bring customer into chat
        </h3>
      </div>
      <div class="px-4 py-3 space-y-3 text-xs text-n-slate-12">
        <p class="text-n-slate-11 leading-relaxed">
          This closes the customer's optional-context panel and pulls
          <span class="font-medium text-n-slate-12">{{ flagName }}</span>
          into the live chat. Their entered details are saved, not lost. The
          action is audited.
        </p>
        <div>
          <label
            class="block text-[10px] text-n-slate-11 uppercase tracking-wide mb-1"
            >Reason (optional)</label>
          <input
            v-model="overrideReason"
            type="text"
            maxlength="200"
            placeholder="e.g. ready to help now"
            class="w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1.5 text-xs text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </div>
        <div
          v-if="overrideResult && overrideResult !== 'ok'"
          class="text-[11px] text-n-ruby-11"
        >
          {{ overrideResult }}
        </div>
      </div>
      <div
        class="px-4 py-3 border-t border-n-weak flex items-center justify-end gap-2"
      >
        <button
          class="text-xs text-n-slate-11 hover:text-n-slate-12 px-3 py-1.5"
          :disabled="overrideBusy"
          @click="closeOverrideModal"
        >
          Cancel
        </button>
        <button
          class="text-xs font-semibold text-white bg-n-brand rounded-md px-3 py-1.5 hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
          :disabled="overrideBusy"
          @click="submitOverride"
        >
          <Spinner v-if="overrideBusy" size="14" class="text-white" />
          <span>Bring into chat</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===========================================================================
 * #17 "Support cockpit" design polish -- structured intel panel.
 * Sapphire accent (#2563EB) defined as a component-scoped variable so the
 * cockpit accent is consistent without inventing global design tokens or
 * leaning on !important. Falls back gracefully if the var is missing.
 * ======================================================================== */
.mvp-customer-intel {
  font-size: 12px;
  --mvp-sapphire: #2563eb;
  --mvp-sapphire-strong: #1d4ed8;
  --text-n-slate-9: #1e40af;
  --mvp-sapphire-tint: rgba(37, 99, 235, 0.08);
  --mvp-sapphire-tint-strong: rgba(37, 99, 235, 0.14);
}

/* Phase 3: Immediate Attention -- state-driven semantic left accent (calm by default,
   warning amber, danger red). Subtle tint; the card stands apart without shouting. */
.mvp-ia-calm { border-left: 2px solid rgba(120, 130, 150, 0.45); } /* neutral left-bar; strong color reserved for LIVE amber only */
.mvp-ia-warning { border-left: 2px solid #d97706; background: rgba(217, 119, 6, 0.06); }
.mvp-ia-danger { border-left: 2px solid #e11d48; background: rgba(225, 29, 72, 0.07); }

/* Sapphire accent helpers (scoped; reference the panel variable) */
.text-n-slate-9 {
  color: var(--text-n-slate-9);
}
.mvp-sapphire-link:hover {
  color: var(--mvp-sapphire-strong);
}
.mvp-sapphire-btn {
  background-color: var(--mvp-sapphire);
}
.mvp-sapphire-btn:hover:not(:disabled) {
  background-color: var(--mvp-sapphire-strong);
}

/* Routing group badge -- sapphire tile */
.mvp-routing-badge {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

/* Relabel select -- sapphire focus ring */
.mvp-relabel-select:focus {
  border-color: var(--mvp-sapphire);
  box-shadow: 0 0 0 1px var(--mvp-sapphire);
}

/* Triage summary -- structured intel tile with a sapphire left rule */
.mvp-triage-summary {
  background-color: var(--mvp-sapphire-tint);
  border-left: 2px solid var(--mvp-sapphire);
}

/* Suggested-response chips -- pill with sapphire tint + hover lift */
.mvp-macro-chip {
  background-color: transparent;
  color: inherit;
  border: 1px solid rgba(120, 130, 150, 0.28);
}
.mvp-macro-chip:hover:not(:disabled) {
  background-color: rgba(120, 130, 150, 0.08);
  border-color: rgba(120, 130, 150, 0.5);
}
</style>
